import React from 'react';
import { TDoneVoteRatingGroup, TInteractiveVote, TVotesGroup } from '../../../../../store/interactive/types';
import { useDispatch, useSelector } from 'react-redux';
import { AppStore } from '../../../../../store/applicationState';
import { TDefRequest } from '../../../../../store/Action';

interface TCheckVoteDoneGroup extends TDefRequest {
  data: {
    event_id: number;
    roomId: number;
    votesGroupId: number;
  };
}

interface TSendVote extends TDefRequest {
  data: {
    event_id: number;
    roomId: number;
    votesGroupId: number;
    voteId: number;
    answerId: number;
  };
}

interface Methods<R, S> {
  request: (payload: R) => {
    type: any;
    payload: Object;
  };
  success: (payload: S) => {
    type: any;
    payload: Object;
  };
  error: (message: string) => {
    type: any;
    payload: Object;
  };
}

interface Props {
  CheckVoteDoneGroup: Methods<TCheckVoteDoneGroup, any>;
  SendVote: Methods<TSendVote, void>;
  votesGroup: TVotesGroup;
  done?: { doneVotes: { id: number }[]; points?: number; total?: number } | null;
}

export const useGroup = ({ votesGroup, CheckVoteDoneGroup, SendVote, done }: Props) => {
  const [vote, setVote] = React.useState<TInteractiveVote | null>(null);
  const [end, setEnd] = React.useState(false);
  const [result, setResult] = React.useState<TDoneVoteRatingGroup | undefined>({ doneVotes: [], total: 0, points: 0 });

  const { Sockets } = useSelector((store: AppStore) => store);
  const { room } = Sockets;
  const dispatch = useDispatch();

  const changeVote = React.useCallback((votesGroup: TVotesGroup, step: number) => {
    const vote = votesGroup.votes[step];
    if (vote) {
      setVote(vote);
    } else {
      setEnd(true);
    }
  }, []);

  React.useEffect(() => {
    if (room && votesGroup.id) {
      dispatch(
        CheckVoteDoneGroup.request({
          data: {
            votesGroupId: votesGroup.id,
            event_id: room.event_id,
            roomId: room.absnum,
          },
        })
      );
    }
  }, [dispatch, CheckVoteDoneGroup, votesGroup.id, room]);

  React.useEffect(() => {
    if (done) {
      if (done.doneVotes.length > 0) {
        changeVote(votesGroup, done.doneVotes.length - 1);
      } else {
        changeVote(votesGroup, 0);
      }
    }
  }, [done, votesGroup, changeVote]);

  React.useEffect(() => {
    if (done && votesGroup && vote) {
      setResult({ doneVotes: done.doneVotes, points: done.points || 0, total: done.total || 0 });
      const doneVote = done.doneVotes.find((item) => item.id === vote.id);
      if (doneVote) {
        const index = votesGroup.votes.findIndex((item) => item.id === vote.id);
        changeVote(votesGroup, index + 1);
      }
    }
  }, [done, vote, votesGroup, changeVote]);

  const sendValue = (value: number) => {
    const isDis = value === 0;
    if (!isDis && vote && room) {
      dispatch(
        SendVote.request({
          data: {
            answerId: value,
            voteId: vote.id,
            votesGroupId: votesGroup.id,
            event_id: room.event_id,
            roomId: room.absnum,
          },
          callBack: (success, data) => {
            if (success) {
              const index = votesGroup.votes.findIndex((item) => item.id === vote.id);
              const step = index + 1;
              changeVote(votesGroup, step);
              setResult(data);
            }
          },
        })
      );
    }
  };

  return {
    vote,
    end,
    result,
    sendValue,
  };
};
